import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  LayoutGrid, Briefcase, Cog, BarChart3, Users, Inbox, Settings as SettingsIcon,
  LogOut, Plus, Trash2, Pencil, Eye, EyeOff, Check, X, ArrowLeft,
  Mail, Archive, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "../components/Logo";
import {
  loadCMS, getData, patchSettings, updateSection, subscribe, resetData,
  adminLogin, adminLogout, isAuthed,
} from "../lib/storage";

/* ---------- Bilingual helpers ----------
 * CMS bilingual fields are stored as { es, en }. Legacy data may still be plain
 * strings — `pickStr(v, lang)` normalises both shapes so the admin (always in
 * Spanish) can read them and forms can edit either language side by side.
 */
function pickStr(v, lang = "es") {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "object") return v[lang] || v.es || v.en || "";
  return String(v);
}
function toBilingual(v) {
  if (v && typeof v === "object" && ("es" in v || "en" in v)) {
    return { es: v.es || "", en: v.en || "" };
  }
  return { es: v || "", en: "" };
}

const NAV = [
  { id: "dashboard", label: "Dashboard",     Icon: LayoutGrid },
  { id: "cases",     label: "Casos de éxito", Icon: Briefcase },
  { id: "services",  label: "Servicios",     Icon: Cog },
  { id: "stats",     label: "Estadísticas",  Icon: BarChart3 },
  { id: "team",      label: "Equipo",        Icon: Users },
  { id: "messages",  label: "Mensajes",      Icon: Inbox },
  { id: "settings",  label: "Configuración", Icon: SettingsIcon },
];

const SERVICE_OPTIONS = [
  "Consultoría BA",
  "Automatización",
  "Plataformas",
  "Soluciones AI",
  "Manuales y Procesos",
  "Consultoría BA + Plataformas",
];

export default function Admin() {
  const [authed, setAuthed] = useState(() => isAuthed());

  if (!authed) return <Login onSuccess={() => setAuthed(true)} />;
  return <AdminShell onLogout={() => { adminLogout(); setAuthed(false); }} />;
}

/* ============== LOGIN ============== */
function Login({ onSuccess }) {
  const [pwd, setPwd] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pwd) return;
    setBusy(true);
    setErr("");
    try {
      await adminLogin(pwd);
      await loadCMS();
      onSuccess();
    } catch (ex) {
      setErr(ex.message || "Contraseña incorrecta.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm bg-surface border border-line rounded-lg p-8 relative"
      >
        <Link
          to="/"
          className="absolute top-4 left-4 inline-flex items-center gap-1 text-xs text-muted-fg hover:text-ember"
        >
          <ArrowLeft size={12} /> Volver al sitio
        </Link>
        <div className="flex justify-center">
          <LogoMark className="h-14 w-auto" />
        </div>
        <h1 className="mt-6 font-display uppercase text-primary-fg text-2xl text-center">
          Panel Stratiq
        </h1>
        <p className="mt-2 text-secondary-fg text-sm text-center font-light">
          Ingresa tu contraseña para continuar
        </p>
        <div className="mt-6">
          <input
            data-testid="admin-password-input"
            type="password"
            autoFocus
            className="w-full h-11 px-4 rounded-md bg-bg border border-line focus:border-ember focus:outline-none focus:ring-2 focus:ring-ember/20 text-primary-fg"
            placeholder="Contraseña"
            value={pwd}
            onChange={(e) => { setPwd(e.target.value); setErr(""); }}
          />
          {err && (
            <p data-testid="admin-login-error" className="mt-2 text-sm text-ember">
              {err}
            </p>
          )}
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-login-submit"
          className="mt-5 w-full h-11 rounded-md bg-ember hover:bg-ember/90 text-white font-medium tracking-wide disabled:opacity-60"
        >
          {busy ? "Entrando..." : "Entrar"}
        </button>
      </form>
    </div>
  );
}

/* ============== SHELL ============== */
function AdminShell({ onLogout }) {
  const [active, setActive] = useState("dashboard");
  const [data, setData] = useState(() => getData());
  const [loading, setLoading] = useState(!data);

  useEffect(() => {
    let cancelled = false;
    const off = subscribe((d) => { if (!cancelled) setData(d); });
    loadCMS()
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; off(); };
  }, []);

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-secondary-fg text-xs uppercase tracking-[0.28em]">Cargando panel…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg text-primary-fg flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-line bg-surface sticky top-0 h-screen">
        <div className="p-5 border-b border-line flex items-center gap-3">
          <LogoMark className="h-8 w-auto" />
          <div>
            <div className="font-display uppercase text-primary-fg text-base leading-none">Stratiq</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-fg mt-1">Admin Panel</div>
          </div>
        </div>
        <nav className="flex-1 p-3 flex flex-col gap-1">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`admin-nav-${n.id}`}
              onClick={() => setActive(n.id)}
              className={`flex items-center gap-3 h-10 px-3 rounded-md text-sm font-medium transition-all
                ${active === n.id
                  ? "bg-ember/10 text-ember border-l-2 border-ember pl-[10px]"
                  : "text-secondary-fg hover:text-primary-fg hover:bg-elevated"}`}
            >
              <n.Icon size={16} />
              {n.label}
              {n.id === "messages" && unreadBadge(data)}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-line space-y-1">
          <Link
            to="/"
            target="_blank"
            className="flex items-center gap-3 h-10 px-3 rounded-md text-sm w-full text-secondary-fg hover:text-primary-fg hover:bg-elevated"
          >
            <ExternalLink size={16} /> Ver sitio
          </Link>
          <button
            data-testid="admin-logout"
            onClick={onLogout}
            className="flex items-center gap-3 h-10 px-3 rounded-md text-sm w-full text-secondary-fg hover:text-ember hover:bg-elevated"
          >
            <LogOut size={16} /> Salir
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 h-14 bg-surface border-b border-line flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <LogoMark className="h-7 w-auto" />
          <span className="font-display uppercase text-sm">Admin</span>
        </div>
        <div className="flex gap-2">
          <button onClick={onLogout} className="w-9 h-9 rounded-md border border-line flex items-center justify-center text-ember">
            <LogOut size={14} />
          </button>
        </div>
      </div>

      {/* Mobile tab nav */}
      <div className="md:hidden fixed top-14 inset-x-0 z-30 bg-bg border-b border-line overflow-x-auto">
        <div className="flex gap-1 px-3 py-2">
          {NAV.map((n) => (
            <button
              key={n.id}
              onClick={() => setActive(n.id)}
              className={`shrink-0 h-9 px-3 rounded-md text-xs font-medium transition-colors
                ${active === n.id ? "bg-ember text-white" : "text-secondary-fg border border-line"}`}
            >
              {n.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 pt-28 md:pt-0">
        <div className="p-6 md:p-10 max-w-5xl">
          {active === "dashboard" && <Dashboard data={data} setActive={setActive} />}
          {active === "cases"     && <CasesManager data={data} />}
          {active === "services"  && <ServicesManager data={data} />}
          {active === "stats"     && <StatsManager data={data} />}
          {active === "team"      && <TeamManager data={data} />}
          {active === "messages"  && <MessagesInbox data={data} />}
          {active === "settings"  && <SettingsManager data={data} />}
        </div>
      </main>
    </div>
  );
}

function unreadBadge(data) {
  const unread = data.messages?.filter((m) => !m.read && !m.archived).length || 0;
  if (!unread) return null;
  return (
    <span className="ml-auto inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-ember text-white text-[10px] font-bold">
      {unread}
    </span>
  );
}

/* ============== PAGE TEMPLATE ============== */
function PageHeader({ title, eyebrow, action }) {
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div>
        {eyebrow && (
          <div className="text-ember uppercase text-[11px] tracking-[0.22em] font-medium mb-2">
            {eyebrow}
          </div>
        )}
        <h1 className="font-display uppercase text-primary-fg text-3xl md:text-4xl leading-none">
          {title}
        </h1>
      </div>
      {action}
    </div>
  );
}

/* ============== DASHBOARD ============== */
function Dashboard({ data, setActive }) {
  const stats = [
    { label: "Casos publicados", value: data.cases.filter(c => c.status === "published").length, target: "cases" },
    { label: "Mensajes sin leer", value: data.messages.filter(m => !m.read && !m.archived).length, target: "messages" },
    { label: "Servicios activos", value: data.services.filter(s => s.visible !== false).length, target: "services" },
  ];
  const last = data.meta?.lastUpdated
    ? new Date(data.meta.lastUpdated).toLocaleString("es-MX")
    : "—";

  return (
    <div>
      <PageHeader title="Dashboard" eyebrow="Resumen" />
      <div className="grid sm:grid-cols-3 gap-4">
        {stats.map((s) => (
          <button
            key={s.label}
            onClick={() => setActive(s.target)}
            className="text-left bg-surface border border-line rounded-lg p-6 hover:border-ember/60 transition-colors"
          >
            <div className="text-xs uppercase tracking-[0.22em] text-muted-fg">{s.label}</div>
            <div className="mt-3 font-display text-5xl text-primary-fg leading-none">{s.value}</div>
          </button>
        ))}
      </div>

      <div className="mt-8 bg-surface border border-line rounded-lg p-6">
        <div className="text-xs uppercase tracking-[0.22em] text-muted-fg">Última actualización</div>
        <div className="mt-2 text-primary-fg">{last}</div>
      </div>

      <div className="mt-8 grid sm:grid-cols-3 gap-3">
        {["cases", "services", "settings"].map((id) => {
          const item = NAV.find((n) => n.id === id);
          return (
            <button
              key={id}
              onClick={() => setActive(id)}
              className="flex items-center gap-3 p-5 bg-surface border border-line rounded-lg hover:border-ember/60 transition-colors"
            >
              <item.Icon size={18} className="text-ember" />
              <span className="font-medium text-primary-fg">{item.label}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-12 text-xs text-muted-fg">
        <button
          onClick={async () => {
            if (confirm("¿Restablecer todo el contenido a los valores por defecto? Esta acción no se puede deshacer.")) {
              try {
                await resetData();
                toast.success("Contenido restablecido.");
              } catch (ex) {
                toast.error(ex.message || "No se pudo restablecer.");
              }
            }
          }}
          className="hover:text-ember"
        >
          Restablecer contenido por defecto
        </button>
      </div>
    </div>
  );
}

/* ============== CASES ============== */
function CasesManager({ data }) {
  const [editing, setEditing] = useState(null); // case or 'new'

  const remove = (id) => {
    if (!confirm("¿Eliminar este caso?")) return;
    updateSection("cases", data.cases.filter((c) => c.id !== id));
    toast.success("Caso eliminado.");
  };

  const save = (caseObj) => {
    let next;
    if (caseObj.id) {
      next = data.cases.map((c) => (c.id === caseObj.id ? caseObj : c));
    } else {
      next = [...data.cases, { ...caseObj, id: `c_${Date.now()}` }];
    }
    updateSection("cases", next);
    setEditing(null);
    toast.success("Caso guardado.");
  };

  return (
    <div>
      <PageHeader
        title="Casos de éxito"
        eyebrow="Portafolio"
        action={
          <button
            data-testid="new-case-btn"
            onClick={() => setEditing({})}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-ember hover:bg-ember/90 text-white text-sm font-medium"
          >
            <Plus size={14} /> Nuevo caso
          </button>
        }
      />

      <div className="bg-surface border border-line rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-elevated">
            <tr className="text-left text-muted-fg text-[11px] uppercase tracking-[0.2em]">
              <th className="px-5 py-3 font-medium">Industria</th>
              <th className="px-5 py-3 font-medium">Servicio</th>
              <th className="px-5 py-3 font-medium">Resultado</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium w-24"></th>
            </tr>
          </thead>
          <tbody>
            {data.cases.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-6 text-center text-muted-fg">Sin casos aún.</td></tr>
            )}
            {data.cases.map((c) => (
              <tr key={c.id} data-testid={`case-row-${c.id}`} className="border-t border-line">
                <td className="px-5 py-4 text-primary-fg font-medium">{pickStr(c.industry)}</td>
                <td className="px-5 py-4 text-secondary-fg">{pickStr(c.service)}</td>
                <td className="px-5 py-4 text-secondary-fg max-w-md truncate">{pickStr(c.result)}</td>
                <td className="px-5 py-4">
                  <StatusPill status={c.status} />
                </td>
                <td className="px-5 py-4">
                  <div className="flex gap-1">
                    <IconBtn onClick={() => setEditing(c)} title="Editar"><Pencil size={14} /></IconBtn>
                    <IconBtn onClick={() => remove(c.id)} danger title="Eliminar"><Trash2 size={14} /></IconBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <CaseModal initial={editing} onClose={() => setEditing(null)} onSave={save} />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const isPub = status === "published";
  return (
    <span className={`inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-[11px] uppercase tracking-wider font-medium
      ${isPub ? "bg-ember/15 text-ember" : "bg-elevated text-secondary-fg"}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${isPub ? "bg-ember" : "bg-secondary-fg"}`} />
      {isPub ? "Publicado" : "Borrador"}
    </span>
  );
}

function IconBtn({ children, onClick, danger, title }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`w-8 h-8 rounded-md border border-line flex items-center justify-center transition-colors
        ${danger ? "hover:border-ember hover:text-ember" : "hover:border-primary-fg hover:text-primary-fg"} text-secondary-fg`}
    >
      {children}
    </button>
  );
}

function CaseModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState({
    id: initial.id,
    industry:    toBilingual(initial.industry),
    service:     toBilingual(initial.service || SERVICE_OPTIONS[0]),
    result:      toBilingual(initial.result),
    description: toBilingual(initial.description),
    status: initial.status || "draft",
  });
  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.industry.es || !form.result.es) return toast.error("Industria y resultado en español son requeridos.");
    onSave(form);
  };

  return (
    <Modal onClose={onClose} title={initial.id ? "Editar caso" : "Nuevo caso"}>
      <form onSubmit={submit} className="grid gap-4" data-testid="case-form">
        <BilingualField label="Industria"          value={form.industry}    onChange={setField("industry")} />
        <BilingualField label="Servicio"           value={form.service}     onChange={setField("service")} />
        <BilingualField label="Resultado obtenido" value={form.result}      onChange={setField("result")} />
        <BilingualField label="Descripción corta"  value={form.description} onChange={setField("description")} multi rows={3} />
        <Field label="Status">
          <select className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="case-status-input">
            <option value="published">Publicado</option>
            <option value="draft">Borrador</option>
          </select>
        </Field>
        <div className="flex justify-end gap-2 mt-2">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-md border border-line text-sm">Cancelar</button>
          <button type="submit" data-testid="case-save-btn" className="h-10 px-5 rounded-md bg-ember hover:bg-ember/90 text-white text-sm font-medium">
            Guardar
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ============== SERVICES ============== */
function ServicesManager({ data }) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});

  const startEdit = (s) => {
    setEditingId(s.id);
    setDraft({
      ...s,
      name:      toBilingual(s.name),
      shortDesc: toBilingual(s.shortDesc),
      longDesc:  toBilingual(s.longDesc),
    });
  };
  const cancel = () => { setEditingId(null); setDraft({}); };
  const save = () => {
    updateSection("services", data.services.map((s) => s.id === draft.id ? draft : s));
    cancel();
    toast.success("Servicio actualizado.");
  };
  const toggleVisible = (s) => {
    updateSection("services", data.services.map((x) => x.id === s.id ? { ...x, visible: !x.visible } : x));
  };

  return (
    <div>
      <PageHeader title="Servicios" eyebrow="Catálogo" />
      <div className="grid gap-4">
        {data.services.map((s) => {
          const isEdit = editingId === s.id;
          const cur = isEdit ? draft : s;
          return (
            <div key={s.id} className="bg-surface border border-line rounded-lg p-6">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  {isEdit ? (
                    <div className="grid gap-3 mb-3">
                      <BilingualField label="Nombre"             value={cur.name}      onChange={(v) => setDraft({ ...draft, name: v })} />
                      <BilingualField label="Descripción corta"  value={cur.shortDesc} onChange={(v) => setDraft({ ...draft, shortDesc: v })} multi rows={2} />
                      <BilingualField label="Descripción larga"  value={cur.longDesc}  onChange={(v) => setDraft({ ...draft, longDesc: v })} multi rows={3} />
                      <Field label="Icono (lucide)">
                        <input placeholder="Workflow, Zap, LayoutDashboard, Sparkles, BookOpen…" className={inputCls} value={cur.icon || ""} onChange={(e) => setDraft({ ...draft, icon: e.target.value })} />
                      </Field>
                      <div className="text-xs text-muted-fg">Iconos válidos: Workflow, Zap, LayoutDashboard, Sparkles, BookOpen, Cog, Database, Brain</div>
                    </div>
                  ) : (
                    <>
                      <h3 className="font-display uppercase text-primary-fg text-xl">{pickStr(s.name)}</h3>
                      <p className="mt-2 text-secondary-fg text-sm">{pickStr(s.shortDesc)}</p>
                      {pickStr(s.longDesc) && <p className="mt-2 text-muted-fg text-xs">{pickStr(s.longDesc)}</p>}
                      <div className="mt-3 text-[11px] uppercase tracking-[0.2em] text-muted-fg">Icono: {s.icon}</div>
                    </>
                  )}
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <button onClick={() => toggleVisible(s)} className="w-9 h-9 rounded-md border border-line flex items-center justify-center text-secondary-fg hover:text-ember hover:border-ember/60" title={s.visible ? "Ocultar" : "Mostrar"}>
                    {s.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                  </button>
                  {isEdit ? (
                    <>
                      <button onClick={save} className="w-9 h-9 rounded-md bg-ember text-white flex items-center justify-center" title="Guardar"><Check size={14} /></button>
                      <button onClick={cancel} className="w-9 h-9 rounded-md border border-line flex items-center justify-center" title="Cancelar"><X size={14} /></button>
                    </>
                  ) : (
                    <button onClick={() => startEdit(s)} className="w-9 h-9 rounded-md border border-line flex items-center justify-center text-secondary-fg hover:text-primary-fg" title="Editar"><Pencil size={14} /></button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============== STATS ============== */
function StatsManager({ data }) {
  const [stats, setStats] = useState(data.stats);
  useEffect(() => { setStats(data.stats); }, [data.stats]);

  const update = (id, k, v) => setStats((arr) => arr.map((s) => s.id === id ? { ...s, [k]: v } : s));
  const save = () => {
    // Ensure suffix is stored as { es, en } bilingual object
    const normalized = stats.map((s) => ({ ...s, suffix: toBilingual(s.suffix) }));
    updateSection("stats", normalized);
    toast.success("Estadísticas actualizadas.");
  };

  return (
    <div>
      <PageHeader title="Estadísticas" eyebrow="Barra de métricas"
        action={
          <button onClick={save} className="h-10 px-5 rounded-md bg-ember hover:bg-ember/90 text-white text-sm font-medium">
            Guardar cambios
          </button>
        } />
      <div className="grid gap-4">
        {stats.map((s) => (
          <div key={s.id} className="bg-surface border border-line rounded-lg p-6">
            <div className="grid gap-3">
              <Field label="Número (universal)">
                <input className={inputCls} value={s.number} onChange={(e) => update(s.id, "number", e.target.value)} />
              </Field>
              <BilingualField
                label="Texto descriptivo"
                value={s.suffix}
                onChange={(v) => update(s.id, "suffix", v)}
              />
            </div>
            <div className="mt-4 pl-4 border-l-[3px] border-ember">
              <div className="font-display text-3xl text-primary-fg leading-none">{s.number}</div>
              <div className="text-secondary-fg text-sm mt-1">{pickStr(s.suffix)}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============== TEAM ============== */
function TeamManager({ data }) {
  const [editing, setEditing] = useState(null);

  const save = (m) => {
    let next;
    if (m.id) next = data.team.map((x) => x.id === m.id ? m : x);
    else next = [...data.team, { ...m, id: `t_${Date.now()}` }];
    updateSection("team", next);
    setEditing(null);
    toast.success("Miembro guardado.");
  };
  const remove = (id) => {
    if (!confirm("¿Eliminar miembro?")) return;
    updateSection("team", data.team.filter((m) => m.id !== id));
  };
  const toggleVisible = (m) => {
    updateSection("team", data.team.map((x) => x.id === m.id ? { ...x, visible: !x.visible } : x));
  };

  return (
    <div>
      <PageHeader title="Equipo" eyebrow="Personas"
        action={
          <button onClick={() => setEditing({})} className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-ember hover:bg-ember/90 text-white text-sm font-medium">
            <Plus size={14} /> Nuevo miembro
          </button>
        } />
      <div className="grid gap-4 sm:grid-cols-2">
        {data.team.map((m) => (
          <div key={m.id} className="bg-surface border border-line rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display uppercase text-primary-fg text-lg">{m.name}</div>
                <div className="text-secondary-fg text-sm">{m.title}</div>
              </div>
              <div className="flex gap-1">
                <IconBtn onClick={() => toggleVisible(m)} title="Visibilidad">{m.visible ? <Eye size={14} /> : <EyeOff size={14} />}</IconBtn>
                <IconBtn onClick={() => setEditing(m)} title="Editar"><Pencil size={14} /></IconBtn>
                <IconBtn onClick={() => remove(m.id)} danger title="Eliminar"><Trash2 size={14} /></IconBtn>
              </div>
            </div>
            {m.bio && <p className="mt-3 text-secondary-fg text-sm">{m.bio}</p>}
            {m.tags?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {m.tags.map((t) => (
                  <span key={t} className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border border-line text-secondary-fg">{t}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {editing && <TeamModal initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function TeamModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState({
    id: initial.id,
    name: initial.name || "",
    title: initial.title || "",
    tags: (initial.tags || []).join(", "),
    bio: initial.bio || "",
    photoUrl: initial.photoUrl || "",
    visible: initial.visible !== false,
  });
  const u = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Nombre requerido.");
    onSave({
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    });
  };
  return (
    <Modal onClose={onClose} title={initial.id ? "Editar miembro" : "Nuevo miembro"}>
      <form onSubmit={submit} className="grid gap-3">
        <Field label="Nombre"><input className={inputCls} value={form.name} onChange={u("name")} /></Field>
        <Field label="Cargo"><input className={inputCls} value={form.title} onChange={u("title")} /></Field>
        <Field label="Tags (separados por coma)"><input className={inputCls} value={form.tags} onChange={u("tags")} /></Field>
        <Field label="Bio"><textarea rows={3} className={`${inputCls} h-auto py-3 resize-none`} value={form.bio} onChange={u("bio")} /></Field>
        <Field label="URL de foto (opcional)"><input className={inputCls} value={form.photoUrl} onChange={u("photoUrl")} /></Field>
        <div className="flex justify-end gap-2 mt-2">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded-md border border-line text-sm">Cancelar</button>
          <button type="submit" className="h-10 px-5 rounded-md bg-ember hover:bg-ember/90 text-white text-sm font-medium">Guardar</button>
        </div>
      </form>
    </Modal>
  );
}

/* ============== MESSAGES ============== */
function MessagesInbox({ data }) {
  const [selectedId, setSelectedId] = useState(null);
  const visible = useMemo(() => data.messages.filter((m) => !m.archived), [data.messages]);
  const selected = visible.find((m) => m.id === selectedId) || visible[0];

  useEffect(() => {
    if (selected && !selected.read) {
      const next = data.messages.map((m) => m.id === selected.id ? { ...m, read: true } : m);
      updateSection("messages", next);
    }
    // eslint-disable-next-line
  }, [selected?.id]);

  const archive = (id) => {
    const next = data.messages.map((m) => m.id === id ? { ...m, archived: true } : m);
    updateSection("messages", next);
    setSelectedId(null);
    toast.success("Mensaje archivado.");
  };
  const toggleRead = (id) => {
    const next = data.messages.map((m) => m.id === id ? { ...m, read: !m.read } : m);
    updateSection("messages", next);
  };

  return (
    <div>
      <PageHeader title="Mensajes" eyebrow="Inbox" />
      {visible.length === 0 ? (
        <div className="bg-surface border border-line rounded-lg p-10 text-center">
          <Mail size={32} className="mx-auto text-muted-fg" />
          <p className="mt-3 text-secondary-fg">Sin mensajes nuevos.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-[320px,1fr] gap-4 bg-surface border border-line rounded-lg overflow-hidden">
          <ul className="md:max-h-[600px] overflow-y-auto border-b md:border-b-0 md:border-r border-line">
            {visible.map((m) => (
              <li key={m.id}>
                <button
                  onClick={() => setSelectedId(m.id)}
                  className={`w-full text-left p-4 border-b border-line transition-colors ${
                    selected?.id === m.id ? "bg-elevated" : "hover:bg-elevated/60"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-sm font-medium ${m.read ? "text-secondary-fg" : "text-primary-fg"}`}>
                      {m.name}
                    </span>
                    {!m.read && <span className="w-2 h-2 rounded-full bg-ember" />}
                  </div>
                  <div className="mt-1 text-xs text-muted-fg truncate">{m.service} · {m.company || "—"}</div>
                  <div className="mt-1 text-[11px] text-muted-fg">{new Date(m.receivedAt).toLocaleString("es-MX")}</div>
                </button>
              </li>
            ))}
          </ul>

          {selected && (
            <div className="p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-display uppercase text-primary-fg text-xl">{selected.name}</div>
                  <div className="text-secondary-fg text-sm">{selected.company || "Sin empresa"}</div>
                  <a href={`mailto:${selected.email}`} className="mt-1 inline-block text-ember text-sm">{selected.email}</a>
                </div>
                <div className="flex gap-1">
                  <IconBtn onClick={() => toggleRead(selected.id)} title={selected.read ? "Marcar no leído" : "Marcar leído"}>
                    {selected.read ? <EyeOff size={14} /> : <Eye size={14} />}
                  </IconBtn>
                  <IconBtn onClick={() => archive(selected.id)} title="Archivar"><Archive size={14} /></IconBtn>
                </div>
              </div>
              <div className="mt-4 inline-flex items-center h-7 px-3 rounded-full bg-ember/10 text-ember text-[11px] uppercase tracking-wider font-medium">
                {selected.service}
              </div>
              <div className="mt-5 text-secondary-fg text-[15px] whitespace-pre-wrap leading-relaxed border-t border-line pt-5">
                {selected.message}
              </div>
              <div className="mt-5 text-xs text-muted-fg">
                Recibido: {new Date(selected.receivedAt).toLocaleString("es-MX")}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ============== SETTINGS ============== */
function SettingsManager({ data }) {
  const [form, setForm] = useState(data.settings);
  useEffect(() => setForm(data.settings), [data.settings]);
  const u = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updateBi = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const saveField = (k) => {
    patchSettings({ [k]: form[k] });
    toast.success("Campo guardado.");
  };

  const BILINGUAL_FIELDS = [
    { k: "heroEyebrow",     label: "Hero — Eyebrow",     multi: false },
    { k: "heroHeadline",    label: "Hero — Headline",    multi: true, rows: 2 },
    { k: "heroSubheadline", label: "Hero — Subheadline", multi: true, rows: 3 },
    { k: "aboutParagraph",  label: "About — Párrafo",    multi: true, rows: 5 },
    { k: "ctaHeadline",     label: "CTA — Headline",     multi: false },
    { k: "ctaSubtext",      label: "CTA — Subtexto",     multi: true, rows: 3 },
    { k: "responseTime",    label: "Contacto — Tiempo de respuesta", multi: false },
  ];

  const SINGLE_FIELDS = [
    { k: "contactEmail",    label: "Contacto — Email" },
    { k: "location",        label: "Contacto — Ubicación" },
  ];

  return (
    <div>
      <PageHeader title="Configuración" eyebrow="Contenido global" />
      <div className="grid gap-3">
        {BILINGUAL_FIELDS.map((f) => (
          <div key={f.k} className="bg-surface border border-line rounded-lg p-5">
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <BilingualField
                  label={f.label}
                  value={form[f.k]}
                  onChange={updateBi(f.k)}
                  multi={f.multi}
                  rows={f.rows}
                />
              </div>
              <button
                onClick={() => saveField(f.k)}
                className="mt-7 h-10 px-4 rounded-md bg-ember hover:bg-ember/90 text-white text-sm font-medium shrink-0"
              >
                Guardar
              </button>
            </div>
          </div>
        ))}

        {SINGLE_FIELDS.map((f) => (
          <div key={f.k} className="bg-surface border border-line rounded-lg p-5">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <label className="block text-[11px] uppercase tracking-[0.22em] text-muted-fg mb-2">{f.label}</label>
                <input className={inputCls} value={form[f.k] || ""} onChange={u(f.k)} />
              </div>
              <button
                onClick={() => saveField(f.k)}
                className="mt-7 h-10 px-4 rounded-md bg-ember hover:bg-ember/90 text-white text-sm font-medium shrink-0"
              >
                Guardar
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============== SHARED ============== */
const inputCls =
  "w-full h-11 px-3.5 rounded-md bg-bg border border-line focus:border-ember focus:outline-none focus:ring-2 focus:ring-ember/20 text-primary-fg text-sm";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.22em] text-muted-fg mb-2">{label}</span>
      {children}
    </label>
  );
}

/**
 * BilingualField — pair of ES/EN inputs that map to a { es, en } object.
 * Used everywhere CMS content needs to be available in both languages.
 */
function BilingualField({ label, value, onChange, multi = false, rows = 3, placeholderEs, placeholderEn }) {
  const v = value && typeof value === "object" ? value : { es: value || "", en: "" };
  const update = (lang) => (e) => onChange({ ...v, [lang]: e.target.value });
  const Tag = multi ? "textarea" : "input";
  const commonCls = multi ? `${inputCls} h-auto py-3 resize-none` : inputCls;
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] uppercase tracking-[0.22em] text-muted-fg">{label}</span>
          <span className="text-[10px] uppercase tracking-[0.22em] text-ember font-medium">Español</span>
        </div>
        <Tag
          {...(multi ? { rows } : {})}
          className={commonCls}
          value={v.es || ""}
          onChange={update("es")}
          placeholder={placeholderEs || ""}
          data-testid={`bilingual-es-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] uppercase tracking-[0.22em] text-muted-fg invisible sm:visible">&nbsp;</span>
          <span className="text-[10px] uppercase tracking-[0.22em] text-ember font-medium">English</span>
        </div>
        <Tag
          {...(multi ? { rows } : {})}
          className={commonCls}
          value={v.en || ""}
          onChange={update("en")}
          placeholder={placeholderEn || ""}
          data-testid={`bilingual-en-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}
        />
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-surface border border-line rounded-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display uppercase text-primary-fg text-xl">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-md border border-line flex items-center justify-center"><X size={14} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
